import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { isEmpty, mapValues } from 'lodash';
import {
  RxDrug,
  PrescriptionDTO,
  Drug,
  CocktailOptions,
  Prescription,
  UpdatePrescriptionDTO,
  IOrder
} from '@care-portals/api-interfaces';
import { PrescriptionFacade } from '../../../+store/prescription/prescription.facade';
import { DrugFacade } from '../../../+store/drug/drug.facade';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { hasRole } from '@care-portals/shared';
import { AppFacade } from '../../../+store/app/app.facade';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { LiteJsonEditorComponent } from '@appgen-health-crm/ui';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { CommonModule } from '@angular/common';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { DrugSelectorComponent } from '../../common/drug-selector/drug-selector.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { OrdersFacade } from '../../../+store/order/order.facade';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzInputModule,
    NzDividerModule,
    LiteJsonEditorComponent,
    NzButtonModule,
    NzFormModule,
    NzEmptyModule,
    NzModalModule,
    DrugSelectorComponent
  ],
  selector: 'portals-emr-prescription-details',
  templateUrl: './prescription-details.component.html',
  styleUrls: ['./prescription-details.component.less']
})
export class PrescriptionDetailsComponent implements OnInit, OnDestroy {
  @Input() rx: Prescription;
  @Input() mode: 'edit' | 'create';
  @ViewChild(DrugSelectorComponent, { static: true })
  drugSelector: DrugSelectorComponent;
  @Input() customerId: string;
  @Input() orderId?: number;
  @Input() sendRx?: boolean;

  rxForm = this.formBuilder.group({
    drugs: new FormArray([]),
    notes: [''],
    authorizedBy: [''],
    extras: [{}],
    meta: [{}]
  });

  public get authorizedByCtrl() {
    return this.rxForm.get('authorizedBy');
  }

  selectedDrug: RxDrug;
  isLoading: boolean;
  isSaving: boolean;
  isUpdating: boolean;
  drugs: Drug[];
  isStart = true;
  isDestroyed = new Subject();
  isPharmacy: boolean;
  isOrderAwaitingFulfillmentModalVisible: boolean;
  order: IOrder;
  isDigitalOrder: boolean = false;

  constructor(
    private notification: NzNotificationService,
    private nzDrawerRef: NzDrawerRef,
    private formBuilder: FormBuilder,
    private prescriptionFacade: PrescriptionFacade,
    private drugFacade: DrugFacade,
    private authFacade: AuthFacade,
    private appFacade: AppFacade,
    private orderFacade: OrdersFacade
  ) {
    this.selectedDrug = {} as RxDrug;

    this.prescriptionFacade.creating$.subscribe(res => {
      this.isSaving = res;
      if (!res && !this.isStart) {
        this.nzDrawerRef.close(true);
      }
    });

    this.prescriptionFacade.updating$.subscribe(res => {
      this.isUpdating = res;
      if (!res && !this.isStart) {
        this.nzDrawerRef.close(true);
      }
    });

    this.drugFacade.allDrug$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(res => (this.drugs = res));

    combineLatest([this.authFacade.user$, this.appFacade.selectedLayout$])
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(([user, layout]) => {
        if (!user || !layout) {
          return;
        }
        this.isPharmacy = hasRole(layout?.organization, user, 'pharmacy');

        if (this.isPharmacy) {
          this.authorizedByCtrl.setValidators([Validators.required]);
          this.authorizedByCtrl.updateValueAndValidity();
          this.authorizedByCtrl.enable();
        } else {
          this.authorizedByCtrl.setValidators([]);
          this.authorizedByCtrl.updateValueAndValidity();
          this.authorizedByCtrl.disable();
        }
      });
  }

  ngOnInit() {
    this.isStart = false;

    if (this.rx) {
      this.rxForm.patchValue({
        drugs: this.rx.drugs,
        notes: this.rx.notes,
        authorizedBy: this.rx.authorizedBy,
        extras: this.rx.extras || {},
        meta: this.rx.meta || {}
      });

      if (this.rx.drugs?.length) {
        this.rx.drugs.forEach(drug => {
          this.addSingleDrug(drug);
        });
      }
    }

    if (this.orderId) {
      this.orderFacade.activeOrder$
        .pipe(
          filter(order => order && String(order.id) === String(this.orderId)),
          takeUntil(this.isDestroyed)
        )
        .subscribe(order => {
          this.order = order;
          this.checkIfDigital();
        });

      this.orderFacade.loadOrder(this.orderId);
    }

    this.rxForm.get('meta').disable();
  }

  ngOnDestroy() {
    this.rxForm.reset();
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }

  onDrugSelected(drug: RxDrug): void {
    this.selectedDrug = drug;
  }

  onDrugAdded(drug: RxDrug) {
    this.selectedDrug = drug;
    this.addDrug();
  }

  addDrug(): void {
    if (isEmpty(this.selectedDrug) || !this.selectedDrug.din) {
      this.notification.info('Attention', 'Please select a drug.');
      return;
    }

    if (this.selectedDrug.mitteType === 'cocktail') {
      const packDrugs: RxDrug[] = (
        this.selectedDrug.options as CocktailOptions[]
      ).map(opt => {
        const _drug = this.drugs.find(x => x.din === opt.din);
        if (!_drug) {
          this.notification.info(
            'Attention',
            'Drug with din: ' + opt.din + ' does not exist'
          );
        } else {
          return {
            din: _drug.din,
            mitte: opt.mitte || _drug.mitte,
            notes: _drug.sig,
            refills: _drug.refills || 0,
            drugName: _drug.name
          };
        }
      });

      this.addPack(packDrugs);
    } else {
      this.addSingleDrug(this.selectedDrug);
    }

    // cleanup
    this.selectedDrug = {} as RxDrug;
    this.drugSelector.clearKeyword();
  }

  addPack(drugs: RxDrug[]) {
    drugs.forEach(drug => {
      this.addSingleDrug(drug);
    });
  }

  addSingleDrug(drug: RxDrug) {
    if (!this.checkInCatalog(drug.din)) {
      this.notification.info(
        'Attention',
        'This din is not in the drug catalog'
      );
      return;
    }

    const existProduct = this.rxForm.value.drugs.filter(
      _drug => _drug.din === drug.din
    );

    if (existProduct.length > 0) {
      this.notification.info('Attention', 'Already exist');
      return;
    }

    this.drugsControl.insert(
      this.drugsControl.controls.length,
      new FormGroup({
        din: new FormControl(drug.din, Validators.required),
        mitte: new FormControl(drug.mitte, [
          Validators.required,
          Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')
        ]),
        mitteType: new FormControl(drug.mitteType),
        refills: new FormControl(drug.refills, [
          Validators.required,
          Validators.pattern('^[0-9]*$')
        ]),
        notes: new FormControl(drug.notes, Validators.required),
        drugName: new FormControl(drug.drugName, Validators.required)
      })
    );
  }

  checkInCatalog(din: string): boolean {
    return !!this.drugs.find(drug => drug.din === din);
  }

  get drugsControl(): FormArray {
    return this.rxForm.get('drugs') as FormArray;
  }

  removeDrug(index: number): void {
    this.drugsControl.removeAt(index);
  }

  private checkIfDigital(): void {
    if (!this.order) {
      return;
    }

    this.isDigitalOrder =
      this.order.lineItems?.every(item => item.type === 'digital') ?? false;
  }

  public static touchAll(formGroup: FormGroup | FormArray): void {
    mapValues(formGroup.controls, (c, i) => {
      if (c instanceof FormGroup || c instanceof FormArray) {
        this.touchAll(c);
      } else {
        c['markAsDirty']();
        c['updateValueAndValidity']();
      }
    });
  }

  saveRx() {
    PrescriptionDetailsComponent.touchAll(this.drugsControl);

    if (!this.rxForm.valid) {
      return;
    }
    let value = this.rxForm.value;

    if (!value.drugs.length) {
      this.notification.warning('Warning', 'Please select a drug.');
      return;
    }

    // commenting this out to allow creating RX without a drug note
    // if (this.ngForm.invalid) {
    //   this.message.create('error', 'Make sure all required fields are completed');
    //   return;
    // }

    if (this.mode === 'create') {
      const meta: any = value.meta || {};
      // Only set meta.fromOrderId if order is not digital
      if (this.orderId && !this.isDigitalOrder) {
        meta.fromOrderId = this.orderId;
      }

      const newRx: PrescriptionDTO = {
        drugs: value.drugs,
        customer: this.customerId,
        notes: value.notes,
        authorizedBy: value.authorizedBy,
        extras: value.extras,
        meta: Object.keys(meta).length > 0 ? meta : undefined
      };

      this.prescriptionFacade.createPrescription(
        newRx,
        this.orderId,
        this.sendRx
      );
    } else {
      const updatedRx: UpdatePrescriptionDTO = {
        drugs: value.drugs,
        notes: value.notes,
        authorizedBy: value.authorizedBy,
        extras: value.extras
      };

      this.prescriptionFacade.updatePrescription(this.rx._id, updatedRx);
    }
  }

  voidRx(): void {
    this.prescriptionFacade.updatePrescription(this.rx._id, { voided: true });
  }

  close(): void {
    this.nzDrawerRef.close(true);
  }
}
